/**
 * «Por dónde se vende» and «Por lista de precios» (the owner, 2026-10-13, of what «Análisis» could
 * add: «let's build all of it»): what the counter, the orders and the orders brought to the door
 * each sell and leave; whether delivery pays for itself, what it charged against what transport
 * cost; and what each price list sells and leaves, «Mayorista» beside «Público».
 *
 * Counted as everywhere in «Análisis»: a sale as `netoDeVenta` has it, a line as `netosDeLineas`
 * does (the ticket's discount spread over its lines, what came back off), voided sales nowhere.
 * Pure, so the screen and its tests share one answer.
 */

import type { Centavos } from '@abastece/shared';
import { netoDeVenta, netosDeLineas, type Gasto, type Venta } from '@abastece/core';
import type { Tramo } from './analisis.js';

/** Where a sale came from: the counter, an order, or an order brought to the door with «Envío». */
export type Canal = 'mostrador' | 'pedido' | 'envio';
export const CANALES: readonly Canal[] = ['mostrador', 'pedido', 'envio'];

export const canalDe = (v: Pick<Venta, 'envio' | 'pedidoId'>): Canal =>
  (v.envio ?? 0) > 0 ? 'envio' : v.pedidoId ? 'pedido' : 'mostrador';

const dentro = (fecha: string, t: Tramo): boolean => fecha >= t.desde && fecha <= t.hasta;
const margen = (utilidad: number, ingreso: number): number | null => (ingreso > 0 ? Math.round((utilidad * 10_000) / ingreso) : null);

export interface FilaCanal {
  canal: Canal;
  ventas: number;
  ingreso: Centavos;
  costo: Centavos;
  utilidad: Centavos;
  margenBp: number | null;
  /** What a sale came to on average; null with none. */
  ticket: Centavos | null;
}

/** What each way of selling sold and left in the stretch, those that sold anything, in their order. */
export function porCanal(ventas: readonly Venta[], t: Tramo): FilaCanal[] {
  const filas = new Map<Canal, { ventas: number; ingreso: number; costo: number }>();
  for (const v of ventas) {
    if (v.anulada || !dentro(v.fecha, t)) continue;
    const neto = netoDeVenta(v);
    const f = filas.get(canalDe(v)) ?? { ventas: 0, ingreso: 0, costo: 0 };
    f.ventas += 1;
    f.ingreso += neto.ingreso;
    f.costo += neto.costo;
    filas.set(canalDe(v), f);
  }
  return CANALES.flatMap((canal) => {
    const f = filas.get(canal);
    if (!f) return [];
    const utilidad = f.ingreso - f.costo;
    return [
      {
        canal,
        ventas: f.ventas,
        ingreso: f.ingreso as Centavos,
        costo: f.costo as Centavos,
        utilidad: utilidad as Centavos,
        margenBp: margen(utilidad, f.ingreso),
        ticket: Math.round(f.ingreso / f.ventas) as Centavos,
      },
    ];
  });
}

export interface EnvioDelPeriodo {
  /** What customers paid for having their orders brought: apart from the goods. */
  cobrado: Centavos;
  /** The sales that charged it. */
  entregas: number;
  /** What those sales' goods came to, on average; null with none. */
  promedioPedido: Centavos | null;
  /** «Transporte y combustible» in the stretch, as the expenses recorded it. */
  transporte: Centavos;
}

/**
 * Whether delivery pays for itself: what «Envío» charged in the stretch against what the expenses
 * of «Transporte y combustible» came to. Whoever drives is paid by the payroll, and is not in it.
 */
export function envioDelPeriodo(ventas: readonly Venta[], gastos: readonly Gasto[], t: Tramo): EnvioDelPeriodo {
  let cobrado = 0;
  let entregas = 0;
  let bienes = 0;
  for (const v of ventas) {
    if (v.anulada || !dentro(v.fecha, t) || (v.envio ?? 0) <= 0) continue;
    cobrado += v.envio ?? 0;
    entregas += 1;
    bienes += netoDeVenta(v).ingreso;
  }
  const transporte = gastos.reduce((a, g) => (!g.anulado && g.categoria === 'transporte' && dentro(g.fecha, t) ? a + g.montoCordobas : a), 0);
  return {
    cobrado: cobrado as Centavos,
    entregas,
    promedioPedido: entregas > 0 ? (Math.round(bienes / entregas) as Centavos) : null,
    transporte: transporte as Centavos,
  };
}

export interface FilaLista {
  listaId: string;
  /** The sales with a line at this list's price. */
  ventas: number;
  ingreso: Centavos;
  costo: Centavos;
  utilidad: Centavos;
  margenBp: number | null;
}

/**
 * What each price list sold and left in the stretch, the most first: each line under the list its
 * price came from (its own since 2026-10-11, the sale's before), so a sale with lines from both is in
 * both. Quick sales outside the catalogue are under their sale's list, at no cost.
 */
export function porListaDePrecios(ventas: readonly Venta[], t: Tramo): FilaLista[] {
  const filas = new Map<string, { ventas: Set<string>; ingreso: number; costo: number }>();
  for (const v of ventas) {
    if (v.anulada || !dentro(v.fecha, t)) continue;
    const netos = netosDeLineas(v);
    v.lineas.forEach((l, i) => {
      const listaId = l.listaId ?? v.listaId;
      const f = filas.get(listaId) ?? { ventas: new Set<string>(), ingreso: 0, costo: 0 };
      f.ventas.add(v.id);
      f.ingreso += netos[i]!.ingreso;
      f.costo += netos[i]!.costo;
      filas.set(listaId, f);
    });
  }
  return [...filas.entries()]
    .map(([listaId, f]) => {
      const utilidad = f.ingreso - f.costo;
      return {
        listaId,
        ventas: f.ventas.size,
        ingreso: f.ingreso as Centavos,
        costo: f.costo as Centavos,
        utilidad: utilidad as Centavos,
        margenBp: margen(utilidad, f.ingreso),
      };
    })
    .sort((a, b) => b.ingreso - a.ingreso || a.listaId.localeCompare(b.listaId));
}
